import React from "react";
import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSCardHeader from "views/system-design/DSCardHeader";
import DSTypography from "views/system-design/DSTypography";
import DSChip from "views/system-design/DSChip";
import DSAccordion from "views/system-design/DSAccordion";
import DSAccordionSummary from "views/system-design/DSAccordionSummary";
import DSAccordionDetails from "views/system-design/DSAccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { TestResult, AnalysisIssue, StoryResult } from "../types";
import StoryResultsPanel from "./StoryResultsPanel";

interface ErrorSummaryPanelProps {
  result: TestResult;
}

const severityColor = (severity: AnalysisIssue["severity"]) => {
  switch (severity) {
    case "critical":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "default";
  }
};

/**
 * ErrorSummaryPanel component - displays comprehensive test results
 *
 * Shows:
 * - Overall test status (PASSED/FAILED)
 * - Per-device: status, test metadata, AI analysis, error categories
 * - Test duration
 *
 * Rendered after test completion to provide comprehensive results.
 */
const ErrorSummaryPanel: React.FC<ErrorSummaryPanelProps> = ({ result }) => {
  // Calculate test duration
  const calculateDuration = (): string => {
    if (!result.startedAt || !result.completedAt) {
      return "Unknown";
    }

    const start = new Date(result.startedAt).getTime();
    const end = new Date(result.completedAt).getTime();
    const durationMs = end - start;

    const seconds = Math.floor((durationMs / 1000) % 60);
    const minutes = Math.floor(durationMs / 1000 / 60);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <DSBox sx={{ mt: 4 }}>
      <DSTypography variant="h6" sx={{ mb: 2 }}>
        Test Results
      </DSTypography>

      {/* Overall status */}
      <DSBox sx={{ mb: 2 }}>
        <DSChip
          label={result.overallStatus === "passed" ? "PASSED" : "FAILED"}
          color={result.overallStatus === "passed" ? "success" : "error"}
          size="medium"
        />
      </DSBox>

      {/* Per-device results */}
      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {result.deviceResults.map((deviceResult) => (
          <DSCard key={deviceResult.deviceId}>
            <DSCardHeader
              title={deviceResult.deviceId}
              action={
                <DSBox sx={{ display: "flex", gap: 1 }}>
                  {deviceResult.aiAnalysis && (
                    <DSChip
                      label={
                        deviceResult.aiAnalysis.verdict === "pass"
                          ? "AI: PASS"
                          : "AI: FAIL"
                      }
                      color={
                        deviceResult.aiAnalysis.verdict === "pass"
                          ? "success"
                          : "error"
                      }
                      size="small"
                    />
                  )}
                  <DSChip
                    label={deviceResult.status}
                    color={
                      deviceResult.status === "completed" ? "success" : "error"
                    }
                    size="small"
                  />
                </DSBox>
              }
            />
            <DSCardContent>
              <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Test metadata */}
                <DSBox sx={{ display: "flex", gap: 2 }}>
                  <DSChip
                    label={
                      deviceResult.apkInstalled
                        ? "APK Installed"
                        : "APK Not Installed"
                    }
                    color={deviceResult.apkInstalled ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                  <DSChip
                    label={
                      deviceResult.factoryResetExecuted
                        ? "Factory Reset Done"
                        : "No Factory Reset"
                    }
                    color={
                      deviceResult.factoryResetExecuted ? "info" : "default"
                    }
                    size="small"
                    variant="outlined"
                  />
                </DSBox>

                {/* AI Analysis */}
                {deviceResult.aiAnalysis && (
                  <DSBox
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <DSTypography variant="body1">
                      {deviceResult.aiAnalysis.summary}
                    </DSTypography>

                    <DSTypography variant="body2" color="text.secondary">
                      {deviceResult.aiAnalysis.logEntriesAnalyzed} log entries
                      analyzed
                    </DSTypography>

                    {deviceResult.aiAnalysis.issues.length > 0 && (
                      <DSBox>
                        {deviceResult.aiAnalysis.issues.map((issue, index) => (
                          <DSAccordion key={`ai-${issue.category}-${index}`}>
                            <DSAccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <DSBox
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <DSChip
                                  label={issue.severity}
                                  color={
                                    severityColor(issue.severity) as
                                      | "error"
                                      | "warning"
                                      | "info"
                                      | "default"
                                  }
                                  size="small"
                                />
                                <DSTypography>{issue.category}</DSTypography>
                              </DSBox>
                            </DSAccordionSummary>
                            <DSAccordionDetails>
                              <DSTypography variant="body2" sx={{ mb: 1 }}>
                                {issue.description}
                              </DSTypography>
                              {issue.logExcerpts.length > 0 && (
                                <DSBox
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                  }}
                                >
                                  {issue.logExcerpts.map((excerpt, i) => (
                                    <DSTypography
                                      key={i}
                                      variant="body2"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: "0.85rem",
                                        wordBreak: "break-word",
                                        bgcolor: "grey.100",
                                        p: 0.5,
                                        borderRadius: 1,
                                      }}
                                    >
                                      {excerpt}
                                    </DSTypography>
                                  ))}
                                </DSBox>
                              )}
                            </DSAccordionDetails>
                          </DSAccordion>
                        ))}
                      </DSBox>
                    )}
                  </DSBox>
                )}

                {/* Story results section */}
                {deviceResult.storyResults &&
                  deviceResult.storyResults.length > 0 && (
                    <StoryResultsPanel
                      storyResults={deviceResult.storyResults}
                    />
                  )}

                {/* Total errors */}
                <DSTypography
                  variant="body1"
                  sx={{
                    color:
                      deviceResult.totalErrors > 0
                        ? "error.main"
                        : "success.main",
                    fontWeight: "bold",
                  }}
                >
                  Total errors: {deviceResult.totalErrors}
                </DSTypography>

                {/* Error categories with sample messages */}
                {deviceResult.errors.length > 0 ? (
                  <DSBox>
                    {deviceResult.errors.map((errorSummary, index) => (
                      <DSAccordion key={`${errorSummary.category}-${index}`}>
                        <DSAccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <DSTypography>
                            {errorSummary.category}: {errorSummary.count}{" "}
                            error(s)
                          </DSTypography>
                        </DSAccordionSummary>
                        <DSAccordionDetails>
                          <DSBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {errorSummary.samples.map((sample, sampleIndex) => (
                              <DSTypography
                                key={sampleIndex}
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85rem",
                                  wordBreak: "break-word",
                                }}
                              >
                                {sample.length > 200
                                  ? `${sample.substring(0, 200)}...`
                                  : sample}
                              </DSTypography>
                            ))}
                          </DSBox>
                        </DSAccordionDetails>
                      </DSAccordion>
                    ))}
                  </DSBox>
                ) : (
                  <DSTypography variant="body2" sx={{ color: "success.main" }}>
                    No errors detected
                  </DSTypography>
                )}
              </DSBox>
            </DSCardContent>
          </DSCard>
        ))}
      </DSBox>

      {/* Test duration */}
      <DSBox sx={{ mt: 2 }}>
        <DSTypography variant="body2" color="text.secondary">
          Completed in {calculateDuration()}
        </DSTypography>
      </DSBox>
    </DSBox>
  );
};

export default ErrorSummaryPanel;
